/**
 * Renders one Scene: layers → blend → post-effects (docs/04 "Compositor"). The Renderer keeps
 * one SceneRenderer for the live scene and a second while a transition is running.
 */
import { POSTFX } from '../../shared/postfx';
import type { Scene } from '../../shared/scene';
import { BLEND_MODES } from '../../shared/scene';
import { vizRole } from '../../shared/viz';
import {
  bindTex,
  createDataTexture,
  createRenderTarget,
  deleteRenderTarget,
  uniform,
  unbindUnits,
  type GL,
  type Program,
  type RenderTarget,
  type TargetPool,
} from './gl/gl';
import { UNIT_INPUT, UNIT_PALETTE, type FrameContext } from './frame';
import { CompiledScene, type SourceRegistry } from './ModEvaluator';
import { bakePalette, paletteFromRef } from './palettes';
import type { ProgramCache, VizInstance } from './viz/instances';
import type { VizRegistry } from './viz/registry';

export interface BuiltinPrograms {
  blit: Program;
  composite: Program;
  mirror: Program;
  feedback: Program;
  bright: Program;
  blur: Program;
  uber: Program;
  transition: Program;
  view: Program;
}

export interface SceneDeps {
  gl: GL;
  pool: TargetPool;
  programs: BuiltinPrograms;
  cache: ProgramCache;
  registry: VizRegistry;
  sources: SourceRegistry;
  /** A 1×1 black target used when an effect is off. */
  black: RenderTarget;
  /** Report a layer that can't be drawn (once per instance). */
  onLayerError: (vizName: string, message: string) => void;
}

interface LayerState {
  vizId: string;
  instance: VizInstance;
  /** Previous instance kept drawing until the new one compiles (no black flash on switch). */
  previous: { vizId: string; instance: VizInstance } | null;
  reported?: boolean;
  /** Creation order, so the watchdog can blame the newest layer. */
  created: number;
  /** This layer's own effects (created when it first has some). */
  chain?: EffectChain;
  /** This layer's own palette texture (when it has its own palette). */
  palette?: { tex: WebGLTexture; key: string; cyclic: boolean };
  /** How much bigger it's drawn while zoomed in (see supersampleFor). */
  supersample?: number;
}

type PaletteBind = { tex: WebGLTexture; shift: number; cyclic: boolean };
type FxValue = (fxId: string, paramId: string) => number;

/** Values that leave a picture unchanged, for effects a layer doesn't have. */
const NEUTRAL: Record<string, Record<string, number>> = {
  color: { contrast: 1, saturation: 1 },
};
const neutral = (fxId: string, paramId: string): number => NEUTRAL[fxId]?.[paramId] ?? 0;

/** Upload a palette ref into a 256×1 texture; returns whether it wraps. */
function uploadPalette(gl: GL, tex: WebGLTexture, ref: Scene['palette']): boolean {
  const palette = paletteFromRef(ref);
  gl.activeTexture(gl.TEXTURE0 + UNIT_PALETTE);
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 256, 1, gl.RGBA, gl.UNSIGNED_BYTE, bakePalette(palette));
  return !!palette.cyclic;
}
const paletteKey = (ref: Scene['palette']) =>
  `${ref.id}|${ref.stops?.join(',') ?? ''}|${ref.cyclic ? 1 : 0}`;

/** Start a full-screen pass into `target` (set uniforms, then call end()). */
/**
 * How much bigger to draw a zoomed-in layer: 1, 1.5 or 2, stepping up at once and back down only
 * once the zoom is clearly lower (so a swinging zoom doesn't keep resizing it).
 */
export function supersampleFor(zoom: number, current: number): number {
  const want = zoom > 1.75 ? 2 : zoom > 1.2 ? 1.5 : 1;
  if (want >= current) return want;
  const down = current === 2 ? 1.6 : 1.1;
  return zoom < down ? want : current;
}

function begin(gl: GL, p: Program, target: RenderTarget): Program {
  gl.bindFramebuffer(gl.FRAMEBUFFER, target.fbo);
  gl.viewport(0, 0, target.width, target.height);
  gl.useProgram(p.program);
  return p;
}

function end(gl: GL): void {
  gl.drawArrays(gl.TRIANGLES, 0, 3);
  unbindUnits(gl, UNIT_INPUT, UNIT_INPUT + 1);
}

/**
 * The post-effect chain: mirror/kaleidoscope → trails/feedback → bloom → fused grade. One runs on
 * the finished picture (overall effects); each layer with its own effects has its own, with its
 * own trails memory.
 */
class EffectChain {
  private fbPrev: RenderTarget | null = null;
  private fbNext: RenderTarget | null = null;
  private readonly scratch: RenderTarget[] = [];

  constructor(private readonly deps: SceneDeps) {}

  run(
    fc: FrameContext,
    input: RenderTarget,
    out: RenderTarget,
    fx: FxValue,
    palette: PaletteBind,
    intensity: number,
  ): void {
    const { gl, pool, programs } = this.deps;
    const w = out.width;
    const h = out.height;
    const aspect = w / h;
    const scratch = this.scratch;
    scratch.length = 0;
    let cur = input;

    // Mirror / kaleidoscope.
    const mirrorMode = Math.round(fx('mirror', 'mode'));
    if (mirrorMode > 0) {
      const t = pool.acquire(w, h);
      const p = begin(gl, programs.mirror, t);
      bindTex(gl, p, 'uTex', UNIT_INPUT, cur.texture);
      gl.uniform1i(uniform(gl, p, 'uMode'), mirrorMode);
      gl.uniform1f(uniform(gl, p, 'uSegments'), fx('mirror', 'segments'));
      gl.uniform1f(uniform(gl, p, 'uSpin'), fx('mirror', 'spin'));
      gl.uniform1f(uniform(gl, p, 'uTime'), fc.time);
      gl.uniform1f(uniform(gl, p, 'uAspect'), aspect);
      end(gl);
      scratch.push(t);
      cur = t;
    }

    // Feedback / trails (persistent ping-pong targets).
    if (fx('feedback', 'enabled') >= 0.5 && fc.tier < dropTier('feedback')) {
      this.ensureFeedback(w, h);
      const p = begin(gl, programs.feedback, this.fbNext!);
      bindTex(gl, p, 'uCur', UNIT_INPUT, cur.texture);
      bindTex(gl, p, 'uPrev', UNIT_INPUT + 1, this.fbPrev!.texture);
      gl.uniform1f(uniform(gl, p, 'uAmount'), fx('feedback', 'amount'));
      gl.uniform1f(uniform(gl, p, 'uZoom'), fx('feedback', 'zoom'));
      gl.uniform1f(uniform(gl, p, 'uRotate'), fx('feedback', 'rotate'));
      gl.uniform1f(uniform(gl, p, 'uHue'), fx('feedback', 'hue'));
      gl.uniform1f(uniform(gl, p, 'uAspect'), aspect);
      end(gl);
      cur = this.fbNext!;
      const swap = this.fbPrev;
      this.fbPrev = this.fbNext;
      this.fbNext = swap;
    } else if (this.fbPrev) {
      this.freeFeedback();
    }

    // Bloom at quarter resolution.
    let bloom = this.deps.black;
    const bloomOn =
      fx('bloom', 'enabled') >= 0.5 && fx('bloom', 'strength') > 0 && fc.tier < dropTier('bloom');
    if (bloomOn) {
      const qw = Math.max(1, Math.round(w / 4));
      const qh = Math.max(1, Math.round(h / 4));
      const b1 = pool.acquire(qw, qh);
      const b2 = pool.acquire(qw, qh);
      let p = begin(gl, programs.bright, b1);
      bindTex(gl, p, 'uTex', UNIT_INPUT, cur.texture);
      gl.uniform2f(uniform(gl, p, 'uTexel'), 1 / w, 1 / h);
      gl.uniform1f(uniform(gl, p, 'uThreshold'), fx('bloom', 'threshold'));
      end(gl);
      const r = fx('bloom', 'radius');
      p = begin(gl, programs.blur, b2);
      bindTex(gl, p, 'uTex', UNIT_INPUT, b1.texture);
      gl.uniform2f(uniform(gl, p, 'uDir'), r / qw, 0);
      end(gl);
      p = begin(gl, programs.blur, b1);
      bindTex(gl, p, 'uTex', UNIT_INPUT, b2.texture);
      gl.uniform2f(uniform(gl, p, 'uDir'), 0, r / qh);
      end(gl);
      pool.release(b2);
      scratch.push(b1);
      bloom = b1;
    }

    // Fused grade → out.
    const lensOn = fc.tier < dropTier('lens');
    const p = begin(gl, programs.uber, out);
    bindTex(gl, p, 'uTex', UNIT_INPUT, cur.texture);
    bindTex(gl, p, 'uBloom', UNIT_INPUT + 1, bloom.texture);
    bindTex(gl, p, 'uPalette', UNIT_PALETTE, palette.tex);
    gl.uniform1f(uniform(gl, p, 'uBloomStrength'), bloomOn ? fx('bloom', 'strength') : 0);
    gl.uniform1f(uniform(gl, p, 'uChromatic'), lensOn ? fx('lens', 'chromatic') : 0);
    gl.uniform1f(uniform(gl, p, 'uVignette'), lensOn ? fx('lens', 'vignette') : 0);
    gl.uniform1f(uniform(gl, p, 'uGrain'), lensOn ? fx('lens', 'grain') : 0);
    gl.uniform1f(uniform(gl, p, 'uBrightness'), fx('color', 'brightness'));
    gl.uniform1f(uniform(gl, p, 'uContrast'), fx('color', 'contrast'));
    gl.uniform1f(uniform(gl, p, 'uSaturation'), fx('color', 'saturation'));
    gl.uniform1f(uniform(gl, p, 'uHue'), fx('color', 'hue'));
    gl.uniform1f(uniform(gl, p, 'uGradientMap'), fx('color', 'gradientMap'));
    gl.uniform1f(uniform(gl, p, 'uIntensity'), intensity);
    gl.uniform1f(uniform(gl, p, 'uPaletteShift'), palette.shift);
    gl.uniform1f(uniform(gl, p, 'uPaletteCyclic'), palette.cyclic ? 1 : 0);
    gl.uniform1f(uniform(gl, p, 'uTime'), fc.time);
    gl.uniform1f(uniform(gl, p, 'uAspect'), aspect);
    gl.uniform2f(uniform(gl, p, 'uResolution'), w, h);
    end(gl);
    for (const t of scratch) pool.release(t);
  }

  private ensureFeedback(w: number, h: number): void {
    if (this.fbPrev && this.fbPrev.width === w && this.fbPrev.height === h) return;
    this.freeFeedback();
    this.fbPrev = createRenderTarget(this.deps.gl, w, h);
    this.fbNext = createRenderTarget(this.deps.gl, w, h);
  }

  private freeFeedback(): void {
    if (this.fbPrev) deleteRenderTarget(this.deps.gl, this.fbPrev);
    if (this.fbNext) deleteRenderTarget(this.deps.gl, this.fbNext);
    this.fbPrev = this.fbNext = null;
  }

  dispose(): void {
    this.freeFeedback();
  }
}

let creationCounter = 0;

export class SceneRenderer {
  scene: Scene;
  compiled: CompiledScene;
  private readonly layers = new Map<string, LayerState>();
  private readonly paletteTex: WebGLTexture;
  private paletteKey = '';
  private cyclic = false;
  private defaultIntensity: number;
  private readonly palette: PaletteBind;
  private readonly layerPalette: PaletteBind;
  private readonly overall: EffectChain;
  /**
   * Morph transition: while set, each layer's settings glide from the matching layer in `scene`
   * (same place in the stack, same visualization) to this scene's own, as `p` goes 0 → 1.
   */
  morphFrom: { scene: SceneRenderer; p: number } | null = null;

  constructor(
    private readonly deps: SceneDeps,
    scene: Scene,
    defaultIntensity: number,
  ) {
    this.scene = scene;
    this.defaultIntensity = defaultIntensity;
    this.paletteTex = createDataTexture(deps.gl, 256, deps.gl.RGBA8);
    this.palette = { tex: this.paletteTex, shift: 0, cyclic: false };
    this.layerPalette = { tex: this.paletteTex, shift: 0, cyclic: false };
    this.overall = new EffectChain(deps);
    this.compiled = new CompiledScene(scene, deps.registry.lookup, deps.sources, defaultIntensity);
    this.syncLayers();
    this.syncPalette();
  }

  /** Apply an edited scene in place (parameter tweaks keep their GPU state, e.g. feedback). */
  update(scene: Scene, defaultIntensity = this.defaultIntensity): void {
    this.scene = scene;
    this.defaultIntensity = defaultIntensity;
    this.compiled = new CompiledScene(
      scene,
      this.deps.registry.lookup,
      this.deps.sources,
      defaultIntensity,
    );
    this.syncLayers();
    this.syncPalette();
  }

  /**
   * Glide settings from `from` (already evaluated this frame) towards this scene's: numbers ease
   * across, whole-number settings (like bar counts) step through, menus and switches flip halfway.
   */
  private applyMorph(from: SceneRenderer, p: number): void {
    const e = p * p * (3 - 2 * p);
    const theirs = from.scene.layers;
    this.scene.layers.forEach((layer, i) => {
      const other = theirs[i];
      const a = from.compiled.layers[i];
      const b = this.compiled.layers[i];
      if (!other || other.vizId !== layer.vizId || !a || !b) return;
      if (a.params.effective.length !== b.params.effective.length) return;
      const full = new Map(this.deps.registry.resolve(layer.vizId).params.map((d) => [d.id, d]));
      const to = b.params.effective;
      const fromVals = a.params.effective;
      for (let k = 0; k < to.length; k++) {
        const def = full.get(b.params.defs[k]!.id);
        const x = fromVals[k]!;
        const y = to[k]!;
        if (def?.control === 'select' || def?.control === 'toggle') to[k] = e < 0.5 ? x : y;
        else {
          const v = x + (y - x) * e;
          to[k] = def?.step !== undefined && def.step >= 1 ? Math.round(v) : v;
        }
      }
      const oa = a.opacity.effective[0]!;
      b.opacity.effective[0] = oa + (b.opacity.effective[0]! - oa) * e;
      for (let k = 0; k < b.view.effective.length; k++) {
        const x = a.view.effective[k]!;
        b.view.effective[k] = x + (b.view.effective[k]! - x) * e;
      }
    });
  }

  /** True when every enabled layer has compiled (used to delay transitions until ready). */
  get ready(): boolean {
    for (const l of this.scene.layers) {
      if (!l.enabled) continue;
      const s = this.layers.get(l.id);
      if (s && !s.instance.ready && !s.instance.failed) return false;
    }
    return true;
  }

  private syncLayers(): void {
    const { registry, cache } = this.deps;
    const seen = new Set<string>();
    for (const layer of this.scene.layers) {
      seen.add(layer.id);
      const def = registry.resolve(layer.vizId);
      const cur = this.layers.get(layer.id);
      if (cur && cur.vizId === def.id) continue;
      const instance = registry.create(def, cache);
      const previous = cur ? { vizId: cur.vizId, instance: cur.instance } : null;
      if (cur?.previous) cur.previous.instance.dispose();
      this.layers.set(layer.id, { vizId: def.id, instance, previous, created: ++creationCounter });
    }
    for (const [id, s] of this.layers) {
      if (!seen.has(id)) {
        this.disposeLayer(s);
        this.layers.delete(id);
      }
    }
    // Own palettes and effect chains follow the scene.
    const gl = this.deps.gl;
    for (const layer of this.scene.layers) {
      const st = this.layers.get(layer.id)!;
      if (layer.palette) {
        const key = paletteKey(layer.palette);
        st.palette ??= { tex: createDataTexture(gl, 256, gl.RGBA8), key: '', cyclic: false };
        if (st.palette.key !== key) {
          st.palette.cyclic = uploadPalette(gl, st.palette.tex, layer.palette);
          st.palette.key = key;
        }
      } else if (st.palette) {
        gl.deleteTexture(st.palette.tex);
        delete st.palette;
      }
      if (!layer.postfx && st.chain) {
        st.chain.dispose();
        delete st.chain;
      }
    }
  }

  private disposeLayer(s: LayerState): void {
    s.instance.dispose();
    s.previous?.instance.dispose();
    s.chain?.dispose();
    if (s.palette) this.deps.gl.deleteTexture(s.palette.tex);
  }

  private syncPalette(): void {
    const ref = this.scene.palette;
    const key = paletteKey(ref);
    if (key === this.paletteKey) return;
    this.cyclic = uploadPalette(this.deps.gl, this.paletteTex, ref);
    this.paletteKey = key;
  }

  /** Draw the scene into `out` (same size as fc.width × fc.height). */
  render(fc: FrameContext, sourceValues: Float32Array, out: RenderTarget): void {
    const { gl, pool, programs } = this.deps;
    const w = out.width;
    const h = out.height;
    const c = this.compiled;
    c.evaluate(sourceValues, fc.dt, fc.time, fc.beats);
    if (this.morphFrom) this.applyMorph(this.morphFrom.scene, this.morphFrom.p);
    const palette = this.palette;
    palette.shift = c.paletteShift.effective[0]!;
    palette.cyclic = this.cyclic;
    gl.bindVertexArray(fc.vao);

    // 1. Layers, bottom to top.
    let accum: RenderTarget | null = null;
    const layers = this.scene.layers;
    for (let i = 0; i < layers.length; i++) {
      const layer = layers[i]!;
      if (!layer.enabled) continue;
      const state = this.layers.get(layer.id);
      if (!state) continue;
      let inst = state.instance;
      if (!inst.ready && !inst.failed) {
        if (state.previous?.instance.ready) inst = state.previous.instance;
        else continue;
      } else if (state.previous) {
        state.previous.instance.dispose();
        state.previous = null;
      }
      if (inst.failed) {
        if (!state.reported) {
          state.reported = true;
          this.deps.onLayerError(
            this.deps.registry.resolve(state.vizId).name,
            inst.errorMessage ?? 'failed to load',
          );
        }
        continue;
      }
      const opacity = c.layers[i]!.opacity.effective[0]!;
      if (opacity <= 0.001) continue;
      const cl = c.layers[i]!;
      const view = cl.view.effective;
      // Zoomed in, draw the layer bigger so the zoom stays sharp (in steps, so a moving zoom
      // doesn't resize it every frame).
      const ss = (state.supersample = supersampleFor(view[2]!, state.supersample ?? 1));
      let lt = pool.acquire(Math.round(w * ss), Math.round(h * ss));
      // The layer's own palette, or the overall one.
      let lp: PaletteBind = palette;
      if (state.palette) {
        lp = this.layerPalette;
        lp.tex = state.palette.tex;
        lp.shift = cl.paletteShift?.effective[0] ?? 0;
        lp.cyclic = state.palette.cyclic;
      }
      inst.render(fc, cl.params.effective, lp, lt);
      // Its view: pan, zoom and sharpness.
      if (ss !== 1 || view[0] !== 0 || view[1] !== 0 || view[2] !== 1 || view[3] !== 0) {
        const vt = pool.acquire(w, h);
        const p = begin(gl, programs.view, vt);
        bindTex(gl, p, 'uTex', UNIT_INPUT, lt.texture);
        gl.uniform2f(uniform(gl, p, 'uPan'), view[0]!, view[1]!);
        gl.uniform1f(uniform(gl, p, 'uZoom'), view[2]!);
        gl.uniform1f(uniform(gl, p, 'uSharp'), view[3]!);
        gl.uniform1f(
          uniform(gl, p, 'uMirror'),
          vizRole(this.deps.registry.resolve(layer.vizId)) === 'background' ? 1 : 0,
        );
        gl.uniform2f(uniform(gl, p, 'uTexel'), 1 / lt.width, 1 / lt.height);
        end(gl);
        pool.release(lt);
        lt = vt;
      }
      // The layer's own effects, before it's blended in.
      if (cl.fx) {
        state.chain ??= new EffectChain(this.deps);
        const fxOut = pool.acquire(w, h);
        const fxMap = cl.fx;
        state.chain.run(
          fc,
          lt,
          fxOut,
          (f, prm) => (fxMap.has(f) ? c.layerFxValue(i, f, prm) : neutral(f, prm)),
          lp,
          1,
        );
        pool.release(lt);
        lt = fxOut;
      }
      if (accum === null && layer.blend === 'normal' && opacity >= 0.999) {
        accum = lt;
        continue;
      }
      const base = accum ?? this.deps.black;
      const nt = pool.acquire(w, h);
      {
        const p = begin(gl, programs.composite, nt);
        bindTex(gl, p, 'uBase', UNIT_INPUT, base.texture);
        bindTex(gl, p, 'uLayer', UNIT_INPUT + 1, lt.texture);
        gl.uniform1i(uniform(gl, p, 'uMode'), Math.max(0, BLEND_MODES.indexOf(layer.blend)));
        gl.uniform1f(uniform(gl, p, 'uOpacity'), opacity);
        end(gl);
      }
      if (accum) pool.release(accum);
      pool.release(lt);
      accum = nt;
    }

    const cur: RenderTarget | null = accum;
    if (!cur) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, out.fbo);
      gl.clearColor(0, 0, 0, 1);
      gl.clear(gl.COLOR_BUFFER_BIT);
      return;
    }
    this.overall.run(
      fc,
      cur,
      out,
      (f, prm) => c.fxValue(f, prm),
      palette,
      c.intensity.effective[0]!,
    );
    pool.release(cur);
  }

  /** The most recently created enabled layer (watchdog suspect), preferring non-native kinds. */
  newestLayer(): { layerId: string; name: string; created: number } | null {
    let best: { layerId: string; name: string; created: number; heavy: boolean } | null = null;
    for (const l of this.scene.layers) {
      const s = this.layers.get(l.id);
      if (!l.enabled || !s) continue;
      const def = this.deps.registry.resolve(s.vizId);
      const heavy = (def.kind ?? 'native') !== 'native';
      if (!best || (heavy && !best.heavy) || (heavy === best.heavy && s.created > best.created)) {
        best = { layerId: l.id, name: def.name, created: s.created, heavy };
      }
    }
    return best;
  }

  dispose(): void {
    for (const s of this.layers.values()) this.disposeLayer(s);
    this.layers.clear();
    this.overall.dispose();
    this.deps.gl.deleteTexture(this.paletteTex);
  }
}

function dropTier(fxId: string): number {
  return POSTFX.find((f) => f.id === fxId)?.dropAtTier ?? Infinity;
}
